export type NavIcon =
  | 'dashboard'
  | 'budget'
  | 'supply'
  | 'finance'
  | 'workflow'
  | 'registry'
  | 'settings'

export type NavSection = 'operacional' | 'administrativo'

export interface NavChild {
  label: string
  path: string
}

export interface NavLinkEntry {
  id: string
  label: string
  icon: NavIcon
  section: NavSection
  path: string
  adminOnly?: boolean
}

export interface NavGroupEntry {
  id: string
  label: string
  icon: NavIcon
  section: NavSection
  children: NavChild[]
  adminOnly?: boolean
  badge?: 'aprovacoes'
}

export type NavEntry = NavLinkEntry | NavGroupEntry

/**
 * Estrutura do menu lateral, ordenada pelo fluxo real do negocio:
 * planejar -> comprar -> pagar -> aprovar.
 *
 * O bloco operacional (itens 1 a 5) e o bloco administrativo (itens 6 e 7)
 * sao separados visualmente no Layout.
 */
export const NAVIGATION: NavEntry[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: 'dashboard',
    section: 'operacional',
    path: '/dashboard',
  },
  {
    id: 'orcamento',
    label: 'Orçamento',
    icon: 'budget',
    section: 'operacional',
    children: [
      { label: 'Obras e Centros', path: '/obras' },
      { label: 'Categorias Orçamentárias', path: '/categorias' },
      { label: 'Orçamentos', path: '/orcamento' },
    ],
  },
  {
    id: 'suprimentos',
    label: 'Suprimentos',
    icon: 'supply',
    section: 'operacional',
    children: [
      { label: 'Requisições', path: '/requisicoes' },
      { label: 'Cotações', path: '/cotacoes' },
      { label: 'Pedidos de Compra', path: '/ordens-compra' },
      { label: 'Recebimentos', path: '/recebimentos' },
    ],
  },
  {
    id: 'financeiro',
    label: 'Financeiro',
    icon: 'finance',
    section: 'operacional',
    children: [
      { label: 'Contas a Pagar', path: '/contas-pagar' },
      { label: 'Contas a Receber', path: '/contas-receber' },
      { label: 'Fluxo de Caixa', path: '/fluxo-caixa' },
      { label: 'Conciliação', path: '/conciliacao' },
    ],
  },
  {
    id: 'workflow',
    label: 'Workflow',
    icon: 'workflow',
    section: 'operacional',
    badge: 'aprovacoes',
    children: [
      { label: 'Minhas Aprovações', path: '/aprovacoes' },
      { label: 'Histórico', path: '/auditoria' },
    ],
  },
  {
    id: 'cadastros',
    label: 'Cadastros',
    icon: 'registry',
    section: 'administrativo',
    adminOnly: true,
    children: [
      { label: 'Clientes e Contratos', path: '/clientes' },
      { label: 'Fornecedores', path: '/fornecedores' },
      { label: 'Colaboradores', path: '/colaboradores' },
    ],
  },
  {
    id: 'configuracoes',
    label: 'Configurações',
    icon: 'settings',
    section: 'administrativo',
    adminOnly: true,
    children: [
      { label: 'Usuários e Permissões', path: '/usuarios' },
      { label: 'Parâmetros Gerais', path: '/parametros' },
      { label: 'Regras de Workflow', path: '/regras-workflow' },
    ],
  },
]

/** Rotas novas que ainda nao possuem tela implementada. */
export const PLACEHOLDER_ROUTES: Record<string, { titulo: string; descricao: string }> = {
  '/recebimentos': {
    titulo: 'Recebimentos',
    descricao:
      'Conferência e registro dos recebimentos de material e serviço vinculados aos pedidos de compra.',
  },
  '/contas-receber': {
    titulo: 'Contas a Receber',
    descricao:
      'Títulos a receber por contrato e medição, com previsão de entrada e acompanhamento de inadimplência.',
  },
  '/fluxo-caixa': {
    titulo: 'Fluxo de Caixa',
    descricao:
      'Projeção consolidada de entradas e saídas por obra, com saldo realizado e previsto por período.',
  },
  '/conciliacao': {
    titulo: 'Conciliação',
    descricao:
      'Confronto entre lançamentos internos e extratos bancários, com identificação de divergências.',
  },
  '/parametros': {
    titulo: 'Parâmetros Gerais',
    descricao:
      'Configurações do sistema: dados da empresa, exercício fiscal, moeda e preferências de emissão.',
  },
  '/regras-workflow': {
    titulo: 'Regras de Workflow',
    descricao:
      'Definição das alçadas de aprovação por entidade, etapa e faixa de valor.',
  },
}

function pathMatches(candidate: string, target: string) {
  return candidate === target
}

/** Grupo que contem a rota informada (usado para abrir o accordion na rota ativa). */
export function findGroupForPath(pathname: string): string | null {
  for (const entry of NAVIGATION) {
    if ('children' in entry && entry.children.some(child => pathMatches(child.path, pathname))) {
      return entry.id
    }
  }
  return null
}
